import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { change_Current_Location, changeHeaderTitle } from '../store';
import axios from 'axios';
import { Banknote, ArrowRightLeft, Search, RefreshCw, AlertCircle, CheckCircle } from 'lucide-react';

function ProfitSettlement() {
  const dispatch = useDispatch();
  const MyData = useSelector((state) => state.MyData);

  const [loading, setLoading] = useState(false);
  const [accountBalance, setAccountBalance] = useState(null);
  const [transferAmount, setTransferAmount] = useState('');
  const [targetAccount, setTargetAccount] = useState('');
  const [accountHolder, setAccountHolder] = useState('');
  const [selectedBankCode, setSelectedBankCode] = useState(''); // 기본값: 빈 값
  const [bankInfoList, setBankInfoList] = useState([]);
  const [transferHistory, setTransferHistory] = useState([]);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    dispatch(change_Current_Location("수익 정산"));
    dispatch(changeHeaderTitle({ mainTitle: "수익 정산", subTitle: "모계좌 자산 이체 관리" }));

    // 초기 데이터 로드
    loadAccountBalance();
    loadTransferHistory();
    fetchBankInfo();
  }, []);

  // 토스트 메시지 자동 사라짐
  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 5000); // 5초 후 자동 사라짐

      return () => clearTimeout(timer);
    }
  }, [message.text]);

  // 은행 정보 조회
  const fetchBankInfo = async () => {
    try {
      const response = await axios.get(
        process.env.REACT_APP_API + '/api/payout/bank-info',
        { withCredentials: true }
      );

      if (response.data.code === 1000) {
        setBankInfoList(response.data.data.bank_info || []);
      }
    } catch (error) {
      console.error('은행 정보 조회 오류:', error);
    }
  };

  // 계좌 잔액 조회
  const loadAccountBalance = async () => {
    try {
      setLoading(true);
      const response = await axios.post(process.env.REACT_APP_API + "/api/transfer/balance", {}, {
        withCredentials: true,
      });

      if (response.data.code === 1000) {
        const data = response.data.data;
        setAccountBalance({
          accountNumber: data.account,
          bankCode: data.bank_code,
          totalBalance: data.result.balance_amount,
          availableBalance: data.result.payable_amount
        });
      } else {
        setMessage({ type: 'error', text: response.data.msg || '잔액 조회에 실패했습니다.' });
      }
    } catch (error) {
      console.error('잔액 조회 오류:', error);
      setMessage({ type: 'error', text: '잔액 조회 중 오류가 발생했습니다.' });
    } finally {
      setLoading(false);
    }
  };

  // 예금주 조회
  const handleCheckAccountHolder = async (accountNumber) => {
    if (!accountNumber) return;

    if (!selectedBankCode) {
      setMessage({ type: 'error', text: '은행을 먼저 선택해주세요.' });
      return;
    }

    // 계좌번호 유효성 검증 (10-20자리 숫자)
    const accountRegex = /^\d{10,20}$/;
    if (!accountRegex.test(accountNumber)) {
      setMessage({ type: 'error', text: '계좌번호는 10-20자리 숫자여야 합니다.' });
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(process.env.REACT_APP_API + "/api/transfer/account-holder", {
        bankCode: selectedBankCode,
        account: accountNumber
      }, {
        withCredentials: true,
      });

      if (response.data.code === 1000) {
        setAccountHolder(response.data.data.depositor);
        setMessage({ type: 'success', text: `예금주: ${response.data.data.depositor}` });
      } else {
        setMessage({ type: 'error', text: response.data.msg || '예금주 조회에 실패했습니다.' });
        setAccountHolder('');
      }
    } catch (error) {
      console.error('예금주 조회 오류:', error);
      setMessage({ type: 'error', text: '예금주 조회 중 오류가 발생했습니다.' });
      setAccountHolder('');
    } finally {
      setLoading(false);
    }
  };

  // 이체 실행
  const handleExecuteTransfer = async () => {
    if (!transferAmount || !targetAccount || !selectedBankCode) {
      setMessage({ type: 'error', text: '이체 금액, 은행 선택, 계좌번호를 모두 입력해주세요.' });
      return;
    }

    const amount = parseFloat(transferAmount);

    // 이체 금액 유효성 검증
    if (amount <= 0) {
      setMessage({ type: 'error', text: '이체 금액은 0원보다 커야 합니다.' });
      return;
    }

    if (amount > accountBalance?.availableBalance) {
      setMessage({ type: 'error', text: '이체 금액이 사용 가능한 잔액을 초과합니다.' });
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(process.env.REACT_APP_API + "/api/transfer/company", {
        amount: amount,
        rvBankCode: selectedBankCode,
        rvAccount: targetAccount,
        transferReason: "월간 수익 정산"
      }, {
        withCredentials: true,
      });

      if (response.data.code < 2000) {
        setMessage({ type: 'success', text: '이체가 성공적으로 완료되었습니다.' });
        setTransferAmount('');
        setTargetAccount('');
        setAccountHolder('');
        setSelectedBankCode('');
        // 데이터 새로고침
        loadAccountBalance();
        loadTransferHistory();
      } else {
        setMessage({ type: 'error', text: response.data.message || '이체에 실패했습니다.' });
      }
    } catch (error) {
      console.error('이체 오류:', error);
      setMessage({ type: 'error', text: '이체 중 오류가 발생했습니다.' });
    } finally {
      setLoading(false);
    }
  };

  // 이체 내역 조회
  const loadTransferHistory = async () => {
    try {
      const response = await axios.get(process.env.REACT_APP_API + "/api/transfer/company/logs", {
        withCredentials: true,
      });

      if (response.data.code < 2000) {
        setTransferHistory(response.data.data || []);
      }
    } catch (error) {
      console.error('이체 내역 조회 오류:', error);
    }
  };

  // 금액 포맷팅
  const formatAmount = (amount) => {
    return new Intl.NumberFormat('ko-KR').format(amount);
  };

  // 날짜 포맷팅
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('ko-KR');
  };

  // 은행 코드를 은행명으로 변환
  const getBankName = (bankCode) => {
    const bank = bankInfoList.find(b => b.code === bankCode);
    return bank ? bank.bank_name : bankCode;
  };

  return (
    <div className="profit-settlement-container">
      <div className="page-header">
        <h1>수익 정산</h1>
        <p>모계좌(가상계좌) 자산을 실제 계좌로 이체합니다.</p>
      </div>

      {/* 토스트 메시지 */}
      {message.text && (
        <div className={`toast ${message.type === 'success' ? 'toast-success' : 'toast-error'}`}>
          <div className="toast-content">
            <div className="toast-icon">
              {message.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
            </div>
            <div className="toast-message">
              <div className="toast-title">
                {message.type === 'success' ? '성공' : '오류'}
              </div>
              <div className="toast-text">{message.text}</div>
            </div>
            <button
              className="toast-close"
              onClick={() => setMessage({ type: '', text: '' })}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* 모계좌 정보 (컴팩트) */}
      <div className="account-info-compact">
        <div className="account-header">
          <div className="account-title">
            <Banknote size={20} />
            <span>모계좌 정보</span>
          </div>
          <button
            className="btn-refresh-small"
            onClick={loadAccountBalance}
            disabled={loading}
            title="잔액 새로고침"
          >
            <RefreshCw size={16} className={loading ? 'spinning' : ''} />
          </button>
        </div>
        {accountBalance ? (
          <div className="account-details">
            <div className="account-number">
              <span className="label">계좌:</span>
              <span className="value">{accountBalance.accountNumber}</span>
            </div>
            <div className="account-number">
              <span className="label">은행:</span>
              <span className="value">{getBankName(accountBalance.bankCode)}</span>
            </div>
            <div className="balance-summary">
              <div className="balance-item">
                <span className="label">출금가능:</span>
                <span className="value amount">{formatAmount(accountBalance.availableBalance)}원</span>
              </div>
              <div className="balance-item">
                <span className="label">총잔액:</span>
                <span className="value amount">{formatAmount(accountBalance.totalBalance)}원</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="loading-compact">잔액 정보를 불러오는 중...</div>
        )}
      </div>

      <div className="settlement-grid">

        {/* 이체 폼 */}
        <div className="card">
          <div className="card-header">
            <ArrowRightLeft size={24} />
            <h3>자산 이체</h3>
          </div>
          <div className="card-content">
            <div className="transfer-form">
              <div className="form-group">
                <label>이체 금액 (원)</label>
                <input
                  type="number"
                  value={transferAmount}
                  onChange={(e) => setTransferAmount(e.target.value)}
                  placeholder="이체할 금액을 입력하세요"
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>은행 선택</label>
                <select
                  value={selectedBankCode}
                  onChange={(e) => setSelectedBankCode(e.target.value)}
                  disabled={loading}
                  className="bank-select"
                >
                  <option value="">은행을 선택하세요</option>
                  {bankInfoList.map(bank => (
                    <option key={bank.code} value={bank.code}>
                      {bank.bank_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>이체받을 계좌번호</label>
                <div className="input-with-button">
                  <input
                    type="text"
                    value={targetAccount}
                    onChange={(e) => {
                      // 숫자만 남기고 나머지 제거
                      const cleanedValue = e.target.value.replace(/[^0-9]/g, '');
                      setTargetAccount(cleanedValue);
                    }}
                    placeholder="계좌번호를 입력하세요"
                    disabled={loading}
                  />
                  <button
                    className="btn-check"
                    onClick={() => handleCheckAccountHolder(targetAccount)}
                    disabled={loading || !targetAccount}
                  >
                    <Search size={16} />
                  </button>
                </div>
              </div>

              {accountHolder && (
                <div className="form-group">
                  <label>예금주</label>
                  <div className="account-holder">
                    <CheckCircle size={16} className="text-success" />
                    <span>{accountHolder}</span>
                  </div>
                </div>
              )}

              <button
                className="btn-transfer"
                onClick={handleExecuteTransfer}
                disabled={loading || !transferAmount || !targetAccount || !accountHolder || !selectedBankCode}
              >
                {loading ? '처리 중...' : '이체 실행'}
              </button>
            </div>
          </div>
        </div>

        {/* 이체 내역 */}
        <div className="card">
          <div className="card-header">
            <h3>이체 내역</h3>
            <button
              className="btn-refresh"
              onClick={loadTransferHistory}
              disabled={loading}
            >
              <RefreshCw size={16} className={loading ? 'spinning' : ''} />
            </button>
          </div>
          <div className="card-content">
            {transferHistory.length > 0 ? (
              <div className="transfer-history">
                <table>
                  <thead>
                    <tr>
                      <th>이체일시</th>
                      <th>이체금액</th>
                      <th>은행</th>
                      <th>계좌번호</th>
                      <th>이체사유</th>
                      <th>상태</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transferHistory.map((item, index) => (
                      <tr key={index}>
                        <td>{formatDate(item.createdAt || item.transferDate)}</td>
                        <td className="amount">{formatAmount(item.amount)}원</td>
                        <td>{getBankName(item.rvBankCode)}</td>
                        <td>{item.rvAccount}</td>
                        <td>{item.transferReason || '월간 수익 정산'}</td>
                        <td>
                          <span className={`status ${item.status === 'success' ? 'status-success' :
                            item.status === 'pending' ? 'status-pending' :
                              item.status === 'failed' ? 'status-failed' : 'status-unknown'}`}>
                            {item.status === 'success' ? '성공' :
                              item.status === 'pending' ? '처리중' :
                                item.status === 'failed' ? '실패' : '알 수 없음'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state">이체 내역이 없습니다.</div>
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
                .profit-settlement-container {
                    padding: 20px;
                    margin: 0 auto;
                    margin-top: 80px; /* 헤더 높이만큼 여백 추가 */
                }

                .page-header {
                    margin-bottom: 30px;
                }

                .page-header h1 {
                    font-size: 28px;
                    font-weight: 600;
                    color: #1f2937;
                    margin-bottom: 8px;
                    text-align: left; /* 왼쪽 정렬 */
                }

                .page-header p {
                    color: #6b7280;
                    font-size: 16px;
                    text-align: left; /* 왼쪽 정렬 */
                }

                .toast {
                    position: fixed;
                    top: 20px;
                    right: 20px;
                    z-index: 1000;
                    max-width: 400px;
                    min-width: 300px;
                    animation: slideInRight 0.3s ease-out;
                }

                .toast-content {
                    display: flex;
                    align-items: flex-start;
                    gap: 12px;
                    padding: 16px;
                    border-radius: 8px;
                    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
                    backdrop-filter: blur(10px);
                }

                .toast-success .toast-content {
                    background: rgba(16, 185, 129, 0.95);
                    color: white;
                    border: 1px solid rgba(16, 185, 129, 0.2);
                }

                .toast-error .toast-content {
                    background: rgba(239, 68, 68, 0.95);
                    color: white;
                    border: 1px solid rgba(239, 68, 68, 0.2);
                }

                .toast-icon {
                    flex-shrink: 0;
                    margin-top: 2px;
                }

                .toast-message {
                    flex: 1;
                }

                .toast-title {
                    font-weight: 600;
                    font-size: 14px;
                    margin-bottom: 4px;
                }

                .toast-text {
                    font-size: 13px;
                    opacity: 0.9;
                    line-height: 1.4;
                }

                .toast-close {
                    background: none;
                    border: none;
                    color: white;
                    font-size: 18px;
                    font-weight: bold;
                    cursor: pointer;
                    padding: 0;
                    width: 20px;
                    height: 20px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 50%;
                    transition: background-color 0.2s;
                    flex-shrink: 0;
                }

                .toast-close:hover {
                    background: rgba(255, 255, 255, 0.2);
                }

                @keyframes slideInRight {
                    from {
                        transform: translateX(100%);
                        opacity: 0;
                    }
                    to {
                        transform: translateX(0);
                        opacity: 1;
                    }
                }

                .account-info-compact {
                    background: white;
                    border: 1px solid #e5e7eb;
                    border-radius: 8px;
                    padding: 16px 20px;
                    margin-bottom: 24px;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                }

                .account-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 12px;
                }

                .account-title {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-weight: 600;
                    color: #1f2937;
                }

                .btn-refresh-small {
                    background: #f3f4f6;
                    border: 1px solid #d1d5db;
                    border-radius: 6px;
                    padding: 6px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s;
                }

                .btn-refresh-small:hover {
                    background: #e5e7eb;
                }

                .btn-refresh-small:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .account-details {
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    gap: 12px;
                }

                .account-number {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .account-number .label {
                    font-size: 14px;
                    color: #6b7280;
                    font-weight: 500;
                }

                .account-number .value {
                    font-size: 14px;
                    color: #1f2937;
                    font-weight: 600;
                    font-family: 'Courier New', monospace;
                }

                .balance-summary {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                    align-items: flex-start;
                }

                .balance-item {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }

                .balance-item .label {
                    font-size: 13px;
                    color: #6b7280;
                    font-weight: 500;
                }

                .balance-item .value.amount {
                    font-size: 16px;
                    color: #059669;
                    font-weight: 700;
                }

                .loading-compact {
                    text-align: center;
                    color: #6b7280;
                    font-size: 14px;
                    padding: 8px 0;
                }

                .settlement-grid {
                    display: grid;
                    grid-template-columns: 35% 65%;
                    gap: 20px;
                    margin-bottom: 30px;
                }

                .card {
                    background: white;
                    border-radius: 12px;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                    overflow: hidden;
                }

                .card-header {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 20px;
                    background: #f8fafc;
                    border-bottom: 1px solid #e2e8f0;
                }

                .card-header h3 {
                    font-size: 18px;
                    font-weight: 600;
                    color: #1f2937;
                    margin: 0;
                }

                .btn-refresh {
                    margin-left: auto;
                    background: none;
                    border: none;
                    padding: 8px;
                    border-radius: 6px;
                    cursor: pointer;
                    color: #6b7280;
                    transition: all 0.2s;
                }

                .btn-refresh:hover {
                    background: #e2e8f0;
                    color: #374151;
                }

                .btn-refresh:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .spinning {
                    animation: spin 1s linear infinite;
                }

                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }

                .card-content {
                    padding: 20px;
                }

                .balance-info {
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                }

                .balance-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .balance-item .label {
                    font-weight: 500;
                    color: #6b7280;
                }

                .balance-item .value {
                    font-weight: 600;
                    color: #1f2937;
                }

                .balance-item .amount {
                    font-size: 18px;
                    color: #059669;
                }

                .loading-state {
                    text-align: left; /* 왼쪽 정렬로 변경 */
                    color: #6b7280;
                    padding: 20px;
                }

                .transfer-form {
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                }

                .form-group {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }

                .form-group label {
                    font-weight: 500;
                    color: #374151;
                }

                .form-group input {
                    padding: 12px;
                    border: 1px solid #d1d5db;
                    border-radius: 8px;
                    font-size: 16px;
                    transition: border-color 0.2s;
                }

                .form-group input:focus {
                    outline: none;
                    border-color: #3b82f6;
                    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
                }

                .form-group input:disabled {
                    background-color: #f9fafb;
                    color: #6b7280;
                }

                .bank-select {
                    padding: 12px;
                    border: 1px solid #d1d5db;
                    border-radius: 8px;
                    font-size: 16px;
                    background-color: white;
                    transition: border-color 0.2s;
                }

                .bank-select:focus {
                    outline: none;
                    border-color: #3b82f6;
                    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
                }

                .bank-select:disabled {
                    background-color: #f9fafb;
                    color: #6b7280;
                }

                .input-with-button {
                    display: flex;
                    gap: 8px;
                }

                .input-with-button input {
                    flex: 1;
                }

                .btn-check {
                    padding: 12px 16px;
                    background: #3b82f6;
                    color: white;
                    border: none;
                    border-radius: 8px;
                    cursor: pointer;
                    font-weight: 500;
                    transition: background-color 0.2s;
                }

                .btn-check:hover:not(:disabled) {
                    background: #2563eb;
                }

                .btn-check:disabled {
                    background: #9ca3af;
                    cursor: not-allowed;
                }

                .account-holder {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 12px;
                    background: #f0fdf4;
                    border: 1px solid #bbf7d0;
                    border-radius: 8px;
                    color: #166534;
                    font-weight: 500;
                }

                .text-success {
                    color: #16a34a;
                }

                .btn-transfer {
                    padding: 16px;
                    background: #059669;
                    color: white;
                    border: none;
                    border-radius: 8px;
                    font-size: 16px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: background-color 0.2s;
                }

                .btn-transfer:hover:not(:disabled) {
                    background: #047857;
                }

                .btn-transfer:disabled {
                    background: #9ca3af;
                    cursor: not-allowed;
                }

                .transfer-history table {
                    width: 100%;
                    border-collapse: collapse;
                }

                .transfer-history th,
                .transfer-history td {
                    padding: 12px;
                    text-align: left;
                    border-bottom: 1px solid #e5e7eb;
                }

                .transfer-history th {
                    background: #f9fafb;
                    font-weight: 600;
                    color: #374151;
                }

                .transfer-history .amount {
                    font-weight: 600;
                    color: #059669;
                }

                .status {
                    padding: 4px 8px;
                    border-radius: 4px;
                    font-size: 12px;
                    font-weight: 500;
                }

                .status-success {
                    background: #d1fae5;
                    color: #065f46;
                }

                .status-pending {
                    background: #fef3c7;
                    color: #92400e;
                }

                .status-failed {
                    background: #fee2e2;
                    color: #991b1b;
                }

                .status-unknown {
                    background: #f3f4f6;
                    color: #6b7280;
                }

                .empty-state {
                    text-align: left; /* 왼쪽 정렬로 변경 */
                    color: #6b7280;
                    padding: 40px;
                }

                @media (max-width: 768px) {
                    .account-details {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 12px;
                    }

                    .balance-summary {
                        flex-direction: column;
                        gap: 8px;
                        align-items: flex-start;
                    }

                    .settlement-grid {
                        grid-template-columns: 1fr;
                    }
                    
                    .profit-settlement-container {
                        padding: 16px;
                    }
                }
            `}</style>
    </div>
  );
}

export default ProfitSettlement;
